"use client";

import { cn } from "@/core/lib/utils";
import { useCallback, useMemo, useState } from "react";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";

export type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: "default" | "destructive";
  confirmDisabled?: boolean;
  onConfirm: () => void | Promise<void>;
  /**
   * Classes extras para o conteúdo e o overlay. Necessário quando o diálogo é
   * aberto de dentro de um Sheet, que usa z-[99999] e ficaria por cima do
   * z-[60] padrão.
   */
  className?: string;
  overlayClassName?: string;
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  confirmVariant = "destructive",
  confirmDisabled,
  onConfirm,
  className,
  overlayClassName,
}: ConfirmDialogProps) {
  const [isConfirming, setIsConfirming] = useState(false);

  const effectiveConfirmDisabled = useMemo(() => {
    return confirmDisabled || isConfirming;
  }, [confirmDisabled, isConfirming]);

  const handleConfirm = useCallback(async () => {
    try {
      setIsConfirming(true);
      await onConfirm();
      onOpenChange(false);
    } finally {
      setIsConfirming(false);
    }
  }, [onConfirm, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("max-w-[512px] z-[60]", className)}
        overlayClassName={overlayClassName}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
          {description ? (
            <DialogDescription className="text-sm text-gray-600">
              {description}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogFooter className="flex-row justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-12"
            disabled={effectiveConfirmDisabled}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={handleConfirm}
            className="h-12"
            disabled={effectiveConfirmDisabled}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
